import { EpgUtil } from '../utility/EpgUtil';
import { EpgApi } from '../../api/EpgApi';
import { SettingsKeys } from '../../api/models/TranslationKeys';
import { CmsProperty } from '../../api/models/Shared.types';
import { ListRibbonDataSource } from './ListRibbonDataSource';
import { DateUtil } from '../utility/DateUtil';
import { getDayInfoString } from '../utility/DateFormatter';
import { StaticPageUrls } from '../../api/analytics/ClientEvents';
import { Backend } from '../../api/Backend';
import { ChannelModel } from '../../api/EpgApi.types';

export const ListGroupBy = {
  day: 'day',
  channel: 'channel',
};

const Constants = {
  firstLoadAmount: 4,
  loadAmount: 3,
};

/**
 * @typedef {Object} IFiltersInfo
 * @property {ListGroupBy | string} groupBy
 * @property {ChannelModel} channel
 * @property {Date} date
 * @property {string[]} categories
 */
export interface IFiltersInfo {
  groupBy: string;
  channel: ChannelModel;
  date: Date;
  categories: string[];
}

export class EpgListDataSource {
  private initialized = false;
  private allChannels: ChannelModel[] = [];
  private validChannels: ChannelModel[] = [];
  private replay: boolean | undefined;
  private prevFilterState: IFiltersInfo | undefined;
  private loadedIdx = 0;
  private maxIdx = 0;
  /**
   * @param {Backend} backend
   * @param {IFiltersInfo} filterState
   * @param {boolean} replay
   */
  _initialize(backend: Backend, filterState: IFiltersInfo, replay: boolean) {
    let channelsInit;
    if (this.initialized) {
      channelsInit = Promise.resolve();
    } else {
      channelsInit = backend.cachedChannelApi.validChannels(backend, replay).then(channels => {
        channels.validChannels.forEach(chan => {
          chan.findChannelNumber(backend);
        });

        function compare(a: ChannelModel, b: ChannelModel) {
          if (!a.channelNumber || !b.channelNumber) return 0;
          if (a.channelNumber < b.channelNumber) {
            return -1;
          }
          if (a.channelNumber > b.channelNumber) {
            return 1;
          }
          return 0;
        }

        channels.validChannels.sort(compare);

        const favPromisses = channels.validChannels.map(el => el.updateFavorite(backend));
        return Promise.all(favPromisses).then(() => {
          const favs = channels.validChannels.filter(el => el.favorite);
          const other = channels.validChannels.filter(el => !el.favorite);
          this.validChannels = favs.concat(other);
          this.allChannels = channels.allChannels;
          this.initialized = true;
          this.replay = replay;
        });
      });
    }

    return channelsInit
      .then(() => {
        let resetLazyLoad;
        if (!this.prevFilterState) {
          resetLazyLoad = true;
        } else if (this._filterStateChanged(this.prevFilterState, filterState)) {
          this.prevFilterState = filterState;
          resetLazyLoad = true;
        } else {
          resetLazyLoad = false;
        }
        return resetLazyLoad;
      })
      .then(reset => {
        if (reset) {
          this.loadedIdx = 0;
          this.maxIdx = 0;
        }
      });
  }

  _filterStateChanged(a: IFiltersInfo, b: IFiltersInfo) {
    return (
      !a ||
      !b ||
      a.groupBy !== b.groupBy ||
      a.channel.channelId !== b.channel.channelId ||
      a.date !== b.date ||
      !(
        a.categories.length === b.categories.length &&
        a.categories.every((element, index) => {
          return element === b.categories[index];
        })
      )
    );
  }

  load(backend: Backend, filterState: IFiltersInfo, replay = false) {
    return this._initialize(backend, filterState, replay).then(() => this.createRibbons(backend, filterState));
  }

  loadNext(backend: Backend, filterState: IFiltersInfo) {
    return this.createRibbons(backend, filterState);
  }

  createRibbons(backend: Backend, filterState: IFiltersInfo) {
    if (filterState.groupBy === ListGroupBy.channel) {
      return this.createChannelRibbons(backend, filterState);
    } else if (filterState.groupBy === ListGroupBy.day) {
      return this.createDayRibbons(backend, filterState);
    }
  }

  createDayRibbons(backend: Backend, filterState: IFiltersInfo) {
    const result: ListRibbonDataSource[] = [];
    if (filterState.date) {
      if (this.loadedIdx > 0) return Promise.resolve(result);
      this.maxIdx = 1;
      const fromUntil = EpgUtil.fromUntilFullDay(filterState.date);
      const ribbon = this.makeRibbon(
        backend,
        false,
        filterState.channel,
        this.loadedIdx,
        getDayInfoString(filterState.date, false, backend),
        fromUntil.from,
        fromUntil.until,
        filterState.categories
      );
      this.loadedIdx++;
      result.push(ribbon);
    } else {
      let nrToLoad;
      if (this.loadedIdx === 0) nrToLoad = Constants.firstLoadAmount;
      else nrToLoad = Constants.loadAmount;

      const filterDates = DateUtil.getFilterDates(this.replay);
      this.maxIdx = filterDates.length;

      while (this.loadedIdx < filterDates.length && this.loadedIdx <= this.loadedIdx + nrToLoad) {
        const date = filterDates[this.loadedIdx];
        if (date) {
          const fromUntil = EpgUtil.fromUntilFullDay(date);

          const ribbon = this.makeRibbon(
            backend,
            false,
            filterState.channel,
            this.loadedIdx,
            getDayInfoString(date, false, backend),
            fromUntil.from,
            fromUntil.until,
            filterState.categories
          );
          result.push(ribbon);
          this.loadedIdx++;
        }
      }
    }
    return Promise.resolve(result);
  }

  createChannelRibbons(backend: Backend, filterState: IFiltersInfo) {
    let nrToLoad = 0;
    if (this.loadedIdx === 0) nrToLoad = Constants.firstLoadAmount;
    else nrToLoad = Constants.loadAmount;

    return this.filterChannelsForTags(backend, filterState).then(channels => {
      this.maxIdx = channels?.length || 0;
      const loadUntil = Math.min(this.maxIdx, this.loadedIdx + nrToLoad);

      const result = [];

      for (let i = this.loadedIdx; i < loadUntil; i++) {
        let fromUntil;
        if (filterState.date) {
          fromUntil = EpgUtil.fromUntilFullDay(filterState.date);
        } else {
          fromUntil = EpgUtil.fromUntilFromTime(new Date(), this.replay);
        }

        const channel = channels[i];
        if (channel) {
          const ribbon = this.makeRibbon(
            backend,
            true,
            channel,
            i,
            channel.text.title,
            fromUntil.from,
            fromUntil.until,
            filterState.categories
          );
          result.push(ribbon);
        }
        this.loadedIdx++;
      }
      return result;
    });
  }

  filterChannelsForTags(backend: Backend, filterState: IFiltersInfo): Promise<ChannelModel[]> {
    // if no categories return all channels
    if (!filterState.categories || filterState.categories.length === 0) return Promise.resolve(this.validChannels);

    let fromUntil;
    if (filterState.date) {
      fromUntil = EpgUtil.fromUntilFromTime(filterState.date, this.replay);
    } else {
      fromUntil = EpgUtil.fromUntilFromTime(new Date(), this.replay);
    }

    return EpgApi.channelsWithTags(backend, filterState.categories, fromUntil.from, fromUntil.until).then(channelIds =>
      this.validChannels.filter(channel => channelIds.includes(channel.channelId))
    );
  }

  makeRibbon(
    backend: Backend,
    isChannel: boolean | undefined,
    channel: ChannelModel,
    index: number,
    ribbonTitle: string,
    from: Date,
    until: Date,
    categories: string[]
  ) {
    let properties = backend.globalSettings.stringArray(SettingsKeys.propertiesListRibbon);
    properties = properties
      ? properties
      : [CmsProperty.ShowProgress, CmsProperty.UseBestSchedule, CmsProperty.ShowScheduleInfo];

    //todo channels?
    return new ListRibbonDataSource(
      backend,
      channel,
      from,
      until,
      this.replay,
      categories,
      properties,
      this.allChannels,
      ribbonTitle,
      isChannel
    );
  }

  logPageView(backend: Backend, isReplay: boolean, title: string) {
    backend.logService.trackPageView(isReplay ? StaticPageUrls.replayEpgLists : StaticPageUrls.epgLists, title);
  }
}
